'use client';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/pagination';

import type { UnitPackPricingProps } from '@/core/types';

import { FreeMode } from 'swiper/modules';
import React, { useMemo, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';

import { Box, Button, Container, Typography } from '@/components/ui';
import { UnitCard, BundlesDurations, BundlesCategories } from '@/components/bundles';

import { RouterLink } from '@/routes/components';

export default function UnitPackPricing({
  title,
  subtitle,
  durations = [],
  buttons = [],
  categories = [],
  units = [],
  className = '',
}: UnitPackPricingProps) {
  const [activeDuration, setActiveDuration] = useState(durations.length > 0 ? durations[0].id : '');
  const [activeCategory, setActiveCategory] = useState(0);

  const handleDurationChange = (durationId: string) => {
    setActiveDuration(durationId);
  };

  const renderDurations = () => (
    <BundlesDurations
      durations={durations}
      activeDuration={activeDuration}
      handleDurationChange={handleDurationChange}
    />
  );
  const renderCategories = () => (
    <BundlesCategories
      activeCategory={activeCategory}
      setActiveCategory={setActiveCategory}
      categories={categories}
    />
  );

  // Filter units by selected category
  const filteredUnits = useMemo(() => {
    if (!categories?.length) return units;
    const selectedCategoryId = categories[activeCategory]?.id;
    if (!selectedCategoryId) return units;
    return units.filter((u: any) => {
      const unitCats: string[] | undefined = u.categories;
      if (!unitCats || unitCats.length === 0) return true;
      return unitCats.includes(selectedCategoryId);
    });
  }, [units, categories, activeCategory]);

  const renderUnitCard = (unit: any, indx: number) => {
    const currentTariff =
      unit?.tariffs?.find((price: any) => price.id === activeDuration) || unit?.tariffs?.[0];

    return <UnitCard key={indx} unit={unit} currentTariff={currentTariff} />;
  };

  const renderUnits = () => (
    <Box className="mt-2 md:mt-10">
      <Swiper
        spaceBetween={16}
        modules={[FreeMode]}
        slidesPerView={1.1}
        breakpoints={{
          md: {
            slidesPerView: 2,
            enabled: false,
          },
        }}
        className="!overflow-visible lg:!overflow-hidden"
        wrapperClass="md:!grid grid-cols-2 lg:grid-cols-3 md:gap-6 md:!transform-none md:pb-[84px]"
      >
        {filteredUnits.map((unit, indx) => (
          <SwiperSlide key={indx} className="md:!w-full !h-auto">
            {renderUnitCard(unit, indx)}
          </SwiperSlide>
        ))}
      </Swiper>
    </Box>
  );

  return (
    <Box className={`bg-light-gray/56 pt-[6px] lg:pt-[80px] ${className} `}>
      <Container className="overflow-hidden pb-[40px] pt-[30px]">
        <Box
          className={`flex flex-col xl:flex-row gap-8 justify-between ${
            !categories?.length ? 'md:border-b md:pb-12' : ''
          } ${subtitle ? 'items-center xl:items-end' : 'items-center'}`}
        >
          <Box className="max-w-[826px] flex-grow">
            {title && (
              <Typography
                variant="h2"
                className="text-center xl:text-left text-2xl md:text-[32px] font-bold uppercase"
              >
                {title}
              </Typography>
            )}

            {subtitle && (
              <Typography
                variant="body1"
                className="text-sm md:text-lg text-black font-semibold max-w-2xl mx-auto mt-4 md:mt-[26px] text-center xl:text-left"
              >
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box>{durations.length > 0 && renderDurations()}</Box>
        </Box>

        {categories?.length > 0 && renderCategories()}

        {renderUnits()}

        {buttons && buttons.length > 0 && (
          <Box className="flex justify-center gap-4 mt-8">
            {buttons.map((button, index) => (
              <Button
                key={index}
                href={button.link}
                component={RouterLink}
                variant={button.variant}
                className={`min-w-[171px] md:min-w-[180px] px-2 md:px-4 text-sm md:text-base ${
                  index === 1 ? '!text-black' : ''
                } ${button.className}`}
              >
                {button.text}
              </Button>
            ))}
          </Box>
        )}
      </Container>
    </Box>
  );
}
